import { useContext, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, ListMusic, X } from 'lucide-react';
import { PlayerContext } from '../../../context/PlayerContext';
import { Track } from '../trackList/track';
import './QueuePanel.css';

// A manual scroll animation rather than scrollIntoView({behavior:'smooth'}):
// that API silently goes instant under a "reduce motion" OS/DE setting (or
// just finishes too fast to read over a short distance), which read as a
// jarring snap rather than a glide. This always animates, and does nothing
// if the target is already close enough to centered - so re-running the
// effect for an unrelated queue change doesn't yank the scroll position
// when the active row hasn't actually moved.
//
// animationRef holds the in-flight rAF handle so a track change that lands
// before the previous scroll finished (e.g. clicking through several tracks
// quickly) cancels it first - otherwise two rAF loops both write
// container.scrollTop each frame and fight each other, which is what was
// actually producing the jumpy/snapping motion.
function scrollRowToCenter(
    container: HTMLElement,
    row: HTMLElement,
    animationRef: React.MutableRefObject<number | null>,
    duration = 320
) {
    if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
    }

    // getBoundingClientRect rather than offsetTop, since offsetTop is only
    // relative to container if container happens to be a positioned
    // ancestor - not guaranteed here.
    const containerRect = container.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    const rowCenter = rowRect.top - containerRect.top + container.scrollTop + rowRect.height / 2;
    const targetCenter = rowCenter - container.clientHeight / 2;
    const destination = Math.max(0, Math.min(targetCenter, container.scrollHeight - container.clientHeight));
    const start = container.scrollTop;
    const delta = destination - start;
    if (Math.abs(delta) < 2) return;

    const startTime = performance.now();
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    const step = (now: number) => {
        const progress = Math.min((now - startTime) / duration, 1);
        container.scrollTop = start + delta * easeOutCubic(progress);
        animationRef.current = progress < 1 ? requestAnimationFrame(step) : null;
    };
    animationRef.current = requestAnimationFrame(step);
}

export function QueuePanel(): React.JSX.Element {
    const { queue, queueIndex, reorderQueue, playAtQueueIndex } = useContext(PlayerContext);
    const listRef = useRef<HTMLDivElement>(null);
    const activeRowRef = useRef<HTMLDivElement>(null);
    const scrollAnimationRef = useRef<number | null>(null);

    // Keep the currently playing row centered in the panel whenever it
    // changes track or moves position (e.g. after a reorder). The cleanup
    // cancels any in-flight animation - both on unmount, and before the
    // next run of this same effect (scrollRowToCenter also guards against
    // this internally, but this covers the case where the ref args
    // themselves become stale between one call and the next).
    useEffect(() => {
        if (!listRef.current || !activeRowRef.current) return;
        scrollRowToCenter(listRef.current, activeRowRef.current, scrollAnimationRef);
        return () => {
            if (scrollAnimationRef.current !== null) {
                cancelAnimationFrame(scrollAnimationRef.current);
                scrollAnimationRef.current = null;
            }
        };
    }, [queueIndex, queue]);

    const removeAt = (index: number) => {
        reorderQueue(queue.filter((_, i) => i !== index));
    };

    const moveBy = (index: number, delta: number) => {
        const targetIndex = index + delta;
        if (targetIndex < 0 || targetIndex >= queue.length) return;
        const next = [...queue];
        const [moved] = next.splice(index, 1);
        next.splice(targetIndex, 0, moved);
        reorderQueue(next);
    };

    return (
        <div className="queuePanel">
            <div className="queuePanelHeader">
                <ListMusic size={16} strokeWidth={2.75} />
                <span>Next up{queue.length > 0 ? ` (${queue.length})` : ''}</span>
            </div>

            <div className="queuePanelList" ref={listRef}>
                {queue.length === 0 ? (
                    <div className="queuePanelEmpty">
                        Nothing queued - play an album or add tracks to build a queue.
                    </div>
                ) : (
                    queue.map((track: Track, index: number) => (
                        <div
                            key={`${track.getId()}-${index}`}
                            ref={index === queueIndex ? activeRowRef : undefined}
                            className={`queuePanelRow ${index === queueIndex ? 'queuePanelRowActive' : ''}`}
                        >
                            <span className="queuePanelRowMeta" onClick={() => playAtQueueIndex(index)}>
                                <span className="queuePanelRowTitle">{track.getTitle()}</span>
                                <span className="queuePanelRowArtist">{track.getArtist()}</span>
                            </span>
                            <span className="queuePanelRowActions">
                                <button
                                    className="queuePanelIconButton"
                                    onClick={() => moveBy(index, -1)}
                                    disabled={index === 0}
                                    aria-label="Move up"
                                >
                                    <ChevronUp size={14} strokeWidth={2.75} />
                                </button>
                                <button
                                    className="queuePanelIconButton"
                                    onClick={() => moveBy(index, 1)}
                                    disabled={index === queue.length - 1}
                                    aria-label="Move down"
                                >
                                    <ChevronDown size={14} strokeWidth={2.75} />
                                </button>
                                <button
                                    className="queuePanelIconButton"
                                    onClick={() => removeAt(index)}
                                    aria-label="Remove from queue"
                                >
                                    <X size={14} strokeWidth={2.75} />
                                </button>
                            </span>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
